/**
 * AOI is a required field on broker-submitted deals.
 *
 * Why it is required for brokers but not internally: since de-hub migration
 * 088 an external broker user can be scoped to an AOI list (the "broker lead"
 * tier — see brokerTiers.js), and a deal with no AOI is invisible to every
 * lead in the brokerage. NULLs fail closed on purpose there, so accepting a
 * broker submission without an AOI would quietly create a deal only the
 * brokerage's admins can see. The server enforces the same rule on
 * POST/PUT /deals for any scoped caller (400 `aoi_id is required on
 * broker-submitted deals.`); this is the client half, so the user is told
 * before the round-trip instead of after it.
 *
 * Internal users still file without an AOI — a landman legitimately opens a
 * deal before the area is settled, and the land team can see it regardless.
 *
 * Returns the SAME array when there is nothing to stamp (internal caller, or
 * no `aoi_id` row in this metadata set) so callers can memoize on it without
 * churning downstream identities — matching agentLookupHint.js.
 */

/** The metadata field this applies to. */
export const AOI_FIELD = "aoi_id";

/**
 * Mark the `aoi_id` metadata row required for an external brokerage.
 *
 * @param {Array<object>} metadata deals metadata rows
 * @param {boolean} isBrokerAgent see `isExternalBrokerAgent` in
 *   utils/brokerHiddenFields.js — true for a scoped external broker user of
 *   ANY tier, since the rule is about broker submissions, not about which
 *   tier the submitter happens to be on
 * @returns {Array<object>} metadata (same reference when nothing was stamped)
 */
export const withBrokerRequiredAoi = (metadata, isBrokerAgent) => {
  if (!isBrokerAgent || !Array.isArray(metadata)) return metadata;
  // Casing differs between the two metadata endpoints (v2 is snake_case, the
  // legacy v1 contract is uppercase), so match the way brokerHiddenFields
  // does rather than assuming one.
  const isAoi = (row) =>
    String(row?.field ?? "").toLowerCase() === AOI_FIELD;
  if (!metadata.some((row) => isAoi(row) && !row?.required)) return metadata;
  return metadata.map((row) =>
    isAoi(row) && !row?.required ? { ...row, required: true } : row
  );
};

export default withBrokerRequiredAoi;
